import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	DestroyRef,
	inject,
	Input,
	OnInit,
	signal,
	ViewChild,
	viewChild,
	WritableSignal,
} from '@angular/core';
import { RealEstateService, TenantUnitService as TenantUnitServiceV2 } from '@eadsysteme/ewsrealestateclient-v2';
import { Device, RealEstate, TenantUnitService } from '@eadsysteme/ewsrealestateclient-v1';
import { Owner, Resident, Tenant, TenantUnitDetail, TenantUnitService as TenantUnitServiceV3 } from '@eadsysteme/ewsrealestateclient-v3';
import { UiRoleType } from 'src/app/services/RoleModel';
import { isProtectedRoleType } from '../tenant-unit-detail-legacy/roles-view/roles-view-model';
import { RoleModelService } from '../tenant-unit-detail-legacy/roles-view/role-model.service';
import { PersonDisplayUtil } from '../../util/PersonDisplayUtil';
import { ActivatedRoute, Router } from '@angular/router';
import { SessionService } from '../../services/session.service';
import { TableColumn } from '../../../@vex/interfaces/table-column.interface';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { stagger40ms } from 'src/@vex/animations/stagger.animation';
import { fadeInUp400ms } from 'src/@vex/animations/fade-in-up.animation';
import { Icons } from '../../util/Icons';
import { forkJoin, Observable } from 'rxjs';
import { AuthService, PermissionObject } from '@eadsysteme/ewsauthclient-v1';
import { KeycloakService } from 'src/app/auth/keycloak.service';
import { EnergyEfficiencySettingService, RoleSetting } from '@eadsysteme/energyefficiencyclient-v1';
import { DateUtil } from '../../util/DateUtil';
import { AraLinkComponent } from '@shared/ui/ara-link/ara-link.component';
import { AraButtonComponent } from '@shared/ui/ara-button/ara-button.component';
import { AraPopoverComponent } from '@shared/ui/popover/popover.component';
import { AraRoleHistoryPopoverComponent, RoleHistory, RoleHistoryEntry } from '../util/role-history-popover/role-history-popover.component';
import { PersonEditorDialogComponent } from '../tenant-unit-detail-legacy/roles-view/person-editor-dialog/person-editor-dialog.component';
import { PersonEditorDialogData } from '../tenant-unit-detail-legacy/roles-view/person-editor-dialog/person-editor-dialog.types';
import { EditorTabContext, PersonEditorDataFactory } from '../tenant-unit-detail-legacy/roles-view/person-editor-dialog/person-editor-data.factory';
import { PropertyManagementRightsService } from '../tenant-unit-detail-legacy/roles-view/property-management-rights.service';
import { UviReceptionRightsService } from '../tenant-unit-detail-legacy/roles-view/uvi-reception-rights.service';
import { ConsumptionInformationLegacyComponent } from '../consumption-information-legacy/consumption-information-legacy.component';
import { BreadCrumbService } from '../../../@vex/components/breadcrumbs/BreadCrumbService';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions } from '@angular/material/form-field';
import {
	MatCell,
	MatCellDef,
	MatColumnDef,
	MatHeaderCell,
	MatHeaderCellDef,
	MatHeaderRow,
	MatHeaderRowDef,
	MatNoDataRow,
	MatRow,
	MatRowDef,
	MatTable,
	MatTableDataSource,
} from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { IconHelper } from 'src/app/components/devices/IconHelper';
import { DeviceInspection, DeviceInspectionService } from '@eadsysteme/devicemonitoringclient-v1';
import { SnackbarService } from '../../services/snackbar.service';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Config } from '../../config';
import { debounceTime, distinctUntilChanged, finalize, map } from 'rxjs/operators';
import { AraPaginatorComponent } from '../util/ara-paginator/ara-paginator.component';
import { Billing, BillingCustomCService, BillingPeriod, CustomerAccessStatus } from '@eadsysteme/billingclient-v1';
import { PageLayoutComponent } from '../../../@vex/components/page-layout/page-layout.component';
import { PageLayoutHeaderDirective } from '../../../@vex/components/page-layout/page-layout-header.directive';
import { BreadcrumbsComponent } from '../../../@vex/components/breadcrumbs/breadcrumbs.component';
import { PageLayoutContentDirective } from '../../../@vex/components/page-layout/page-layout-content.directive';
import { TenantDetailCardsComponent } from '../tenant-detail-cards/tenant-detail-cards.component';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import FloorTypeEnum = Device.FloorTypeEnum;
import OperationalStateEnum = DeviceInspection.OperationalStateEnum;
import InspectionStateEnum = DeviceInspection.InspectionStateEnum;
import ReceptionTypeEnum = RoleSetting.ReceptionTypeEnum;

export interface AddressTableEntity {
	street?: string;
	zipCode?: string;
	city?: string;
}

export interface TenantUnitDetailDataSource extends TenantUnitDetail {
	lastInspectionDate?: string;
	nextInspectionDate?: string;
	operationalState?: OperationalStateEnum;
	inspectionState?: InspectionStateEnum;
}

@Component({
	selector: 'ara-real-estate-legacy',
	templateUrl: './real-estate-legacy.component.html',
	styleUrls: ['./real-estate-legacy.component.scss'],
	animations: [fadeInUp400ms, stagger40ms],
	providers: [
		{
			provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
			useValue: {
				appearance: 'outline',
			} as MatFormFieldDefaultOptions,
		},
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [
		TranslocoDirective,
		PageLayoutComponent,
		PageLayoutHeaderDirective,
		BreadcrumbsComponent,
		PageLayoutContentDirective,
		TenantDetailCardsComponent,
		MatButton,
		MatTooltip,
		MatIcon,
		MatIconButton,
		MatInput,
		ReactiveFormsModule,
		MatMenuTrigger,
		MatTable,
		MatSort,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatCheckbox,
		MatCellDef,
		MatCell,
		MatSortHeader,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		MatNoDataRow,
		AraPaginatorComponent,
		MatProgressSpinner,
		MatMenu,
		FormsModule,
		AraLinkComponent,
		AraButtonComponent,
		AraPopoverComponent,
		AraRoleHistoryPopoverComponent,
	],
})
export class RealEstateLegacyComponent implements OnInit, AfterViewInit {
	private readonly activeRoute = inject(ActivatedRoute);
	protected dateUtil = inject(DateUtil);
	private readonly route = inject(Router);
	private readonly tenantUnitService = inject(TenantUnitService);
	private readonly tenantUnitServiceV2 = inject(TenantUnitServiceV2);
	private readonly dialog = inject(MatDialog);
	private readonly energyEfficiencySettingService = inject(EnergyEfficiencySettingService);
	private readonly translocoService = inject(TranslocoService);
	private readonly realEstateService = inject(RealEstateService);
	private readonly snackBar = inject(SnackbarService);
	private readonly authService = inject(AuthService);
	private readonly keycloakService = inject(KeycloakService);
	private readonly deviceInspectionService = inject(DeviceInspectionService);
	protected readonly iconHelper = inject(IconHelper);
	protected readonly sessionService = inject(SessionService);
	protected readonly icons = inject(Icons);
	protected readonly breadCrumbService = inject(BreadCrumbService);
	private readonly billingService = inject(BillingCustomCService);

	private readonly destroyRef = inject(DestroyRef);

	receptionTypes: { [key: string]: ReceptionTypeEnum } = {};
	floorType = FloorTypeEnum;
	getTenantUnits: Observable<TenantUnitDetailDataSource[]>;
	dataSource: MatTableDataSource<TenantUnitDetailDataSource>;
	@ViewChild(MatPaginator) paginator: MatPaginator;
	readonly araPaginator = viewChild(AraPaginatorComponent);
	readonly sort = viewChild(MatSort);
	pageSizeOptions: number[] = [10, 20, 50, 100];
	math = Math;
	currentRealEstateId: string;
	showPrintOptions = false;
	billings: WritableSignal<Billing[]> = signal([]);
	@Input()
	columns: TableColumn<TenantUnitDetailDataSource>[] = [
		{ label: this.translate('tenantunits.number'), property: 'number', type: 'text', visible: true },
		{ label: this.translate('tenantunits.location'), property: 'location', type: 'text', visible: true },
		{
			label: this.translate('tenantunits.apartmentNumber'),
			property: 'apartmentNumber',
			type: 'text',
			visible: true,
		},
		{
			label: this.translate('tenantunits.owner'),
			property: 'currentOwner',
			type: 'text',
			visible: true,
		},
		{ label: this.translate('tenantunitDetails.rolesView.showTenant'), property: 'currentTenant', type: 'text', visible: true },
		{ label: this.translate('tenantunits.resident'), property: 'currentResident', type: 'text', visible: true },
		{
			label: this.translate('tenantunitDetails.printUvi'),
			property: 'uviDelivery',
			type: 'text',
			visible: true,
		},
		{ label: this.translate('address.street'), property: 'address', type: 'text', visible: true },
	];
	finishedLoading = signal(false);
	protected readonly Math = Math;
	readonly UiRoleType = UiRoleType;
	private readonly currentArea: number;
	private readonly tenantUnitServiceV3 = inject(TenantUnitServiceV3);
	private readonly roleModelService = inject(RoleModelService);
	private readonly personEditorDataFactory = inject(PersonEditorDataFactory);
	private readonly uviReceptionRightsService = inject(UviReceptionRightsService);
	private readonly propertyManagementRightsService = inject(PropertyManagementRightsService);
	protected readonly personDisplayUtil = inject(PersonDisplayUtil);
	private readonly currentRealEstate: WritableSignal<RealEstate> = signal(null);
	protected readonly isPropertyManagement = signal(false);

	isMainRoute = signal(false);
	isSmokeDetectorRoute = signal(false);

	selection = new SelectionModel<TenantUnitDetail>(true, []);
	selectedTenantunitIds: Set<string> = new Set();
	deSelectedTenantunitIds: Set<string> = new Set();
	selectToggleEnabled = signal(false);
	allSelected = signal(false);
	private readonly realestateById = new Map<string, TenantUnitDetail>();
	searchValueFC = new FormControl();
	searchTimeoutDelay = Config.SEARCH_DELAY;

	constructor() {
		this.currentArea = Number(sessionStorage.getItem('selectedRealEstateArea') ?? this.sessionService.effectiveArea);
		this.isMainRoute.set(!this.isSmokeDetectorRoute());
		this.addColumns();
	}

	get visibleColumns() {
		return this.columns.filter((column) => column.visible).map((column) => column.property);
	}

	ngOnInit(): void {
		this.currentRealEstateId = this.activeRoute.snapshot.paramMap.get('realEstateId');
		this.propertyManagementRightsService
			.resolve()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((isPropertyManagement) => this.isPropertyManagement.set(isPropertyManagement));
		this.realEstateService
			.getRealEstate(this.currentArea, this.currentRealEstateId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (realEstate) => {
					this.currentRealEstate.set(realEstate);
					this.loadBillings(realEstate.id);
				},
			});
		if (!this.currentRealEstateId) {
			this.currentRealEstateId = sessionStorage.getItem('selectedRealEstateID');
		}
		// v3 populates currentTenant.person but NOT currentResident.person in the list response.
		// v2 populates currentResident with flat fields (given/family/fullName) but NOT currentTenant.
		// Merge both to get complete data for all role columns.
		this.getTenantUnits = forkJoin([
			this.tenantUnitServiceV3.getByRealEstateId(this.currentArea, this.currentRealEstateId),
			this.tenantUnitServiceV2.getByRealEstateId(this.currentArea, this.currentRealEstateId),
		]).pipe(
			map(([v3Units, v2Units]) =>
				v3Units.map((v3Unit) => {
					const enriched = { ...v3Unit } as TenantUnitDetailDataSource;
					// If v3 didn't populate currentResident.person, supplement from v2 flat fields
					if (
						!enriched.currentResident?.person?.fullName &&
						!enriched.currentResident?.person?.givenName &&
						!enriched.currentResident?.person?.familyName
					) {
						const v2Unit = v2Units.find((u) => u.id === v3Unit.id);
						const v2Res = v2Unit?.currentResident as any;
						if (v2Res?.given || v2Res?.family || v2Res?.fullName) {
							enriched.currentResident = {
								...(enriched.currentResident ?? {}),
								id: enriched.currentResident?.id ?? v2Res.id,
								validFrom: enriched.currentResident?.validFrom ?? v2Res.validFrom,
								validTo: enriched.currentResident?.validTo ?? v2Res.validTo,
								person: {
									givenName: v2Res.given,
									familyName: v2Res.family,
									fullName: v2Res.fullName,
								},
							} as any;
						}
					}
					return enriched;
				})
			)
		) as Observable<TenantUnitDetailDataSource[]>;
		this.finishedLoading.set(false);
		this.getTenantUnits
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				finalize(() => {
					this.finishedLoading.set(true);
				})
			)
			.subscribe({
				next: (tenantUnits) => this.bindTenantUnits(tenantUnits),
			});

		sessionStorage.setItem('selectedTenantUnitID', '');
		sessionStorage.setItem('activeView', globalThis.location.href);
		this.setBreadCrumbs();

		if (this.sessionService.isPropertyManagement() || this.sessionService.isDevRole() || this.sessionService.isSupportRole()) {
			this.showPrintOptions = true;
		}

		this.searchValueFC.valueChanges
			.pipe(takeUntilDestroyed(this.destroyRef), debounceTime(this.searchTimeoutDelay), distinctUntilChanged())
			.subscribe(() => {
				this.applyFilter(this.searchValueFC.value);
			});
	}

	ngAfterViewInit() {
		this.paginator = this.araPaginator()?.paginator();
	}

	translate(key: string): string {
		return this.translocoService.translate(key);
	}

	onTenantUnitDetail(tenantUnit: TenantUnitDetailDataSource): void {
		sessionStorage.setItem('selectedTenantUnitID', tenantUnit.id);
		const tokenParsed = this.keycloakService.getKeycloakInstance().tokenParsed;
		if (this.sessionService.isPropertyManagement() || this.sessionService.isDevRole() || this.sessionService.isSupportRole()) {
			if (this.isSmokeDetectorRoute()) {
				if (tenantUnit.inspectionState === undefined || tenantUnit.inspectionState === InspectionStateEnum.READY) {
					this.snackBar.warn(this.translate('smokeDetectorCheck.noData'));
				} else {
					const id = tenantUnit.id ?? 'superordinate';
					sessionStorage.setItem('selectedTenantUnitID', id);
					this.route.navigate(['/smoke-detector-checks/realestates/' + this.currentRealEstateId + '/tenantunits/' + tenantUnit.id]);
				}
			} else {
				sessionStorage.setItem('selectedTenantUnitID', tenantUnit.id);
				this.route.navigate(['/realestates/' + this.currentRealEstateId + '/tenantunits/' + tenantUnit.id]);
			}
		} else {
			this.authService
				.getPermissionTypeForUserAndTenantUnitId(this.currentArea, tokenParsed.sub, tenantUnit.id)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe((value) => {
					if (value.length === 0) {
						this.route.navigate(['/permissions']);
					} else if (value.includes(PermissionObject.PermissionTypeEnum.OWNER)) {
						this.route.navigate(['/realestates/' + this.currentRealEstateId + '/tenantunits/' + tenantUnit.id]);
					} else if (value.includes(PermissionObject.PermissionTypeEnum.RESIDENT)) {
						this.route.navigate(['/uvi']);
					}
				});
		}
	}

	toggleColumnVisibility(column, event) {
		event.stopPropagation();
		event.stopImmediatePropagation();
		column.visible = !column.visible;
	}

	applyFilter(filterValue: string) {
		filterValue = filterValue.trim(); // Remove whitespace
		filterValue = filterValue.toLowerCase(); // Datasource defaults to lowercase matches
		this.dataSource.filter = filterValue;
		if (this.paginator) {
			this.paginator.firstPage();
		}
	}

	openConsumptionInformations() {
		this.dialog.open(ConsumptionInformationLegacyComponent, {
			width: '1000px',
			data: this.currentRealEstateId,
			autoFocus: false,
			restoreFocus: false,
		});
	}

	beautifyDate(dateString: string): string {
		return this.dateUtil.setDateFormatByActiveLang(dateString, '');
	}

	isCurrentRole(role: { validTo?: string } | undefined): boolean {
		if (!role) return false;
		if (!role.validTo || role.validTo.startsWith('9999')) return true;
		return role.validTo >= new Date().toISOString().slice(0, 10);
	}

	getOwnerDisplayName(tenantUnit: TenantUnitDetailDataSource): string {
		const owner = tenantUnit.currentOwner;
		return owner?.person && this.isCurrentRole(owner) ? this.personDisplayUtil.getShortNameForOverview(owner.person) : '';
	}

	getResidentDisplayName(tenantUnit: TenantUnitDetailDataSource): string {
		const resident = tenantUnit.currentResident;
		return resident?.person && this.isCurrentRole(resident) ? this.personDisplayUtil.getShortNameForOverview(resident.person) : '';
	}

	getTenantDisplayName(tenantUnit: TenantUnitDetailDataSource): string {
		const tenant = tenantUnit.currentTenant;
		return tenant?.person && this.isCurrentRole(tenant) ? this.personDisplayUtil.getShortNameForOverview(tenant.person) : '';
	}

	/** Ohne aktuellen Bewohner gibt es keine uVI-Daten. */
	canJumpToUvi(tenantUnit: TenantUnitDetailDataSource): boolean {
		return !!tenantUnit.currentResident;
	}

	uviJumpTooltipKey(tenantUnit: TenantUnitDetailDataSource): string {
		return this.canJumpToUvi(tenantUnit) ? 'uvi.jumpToUvi' : 'uvi.jumpToUviNoResident';
	}

	routeToUvi(tenantUnit: TenantUnitDetailDataSource, event?: Event): void {
		event?.stopPropagation();
		sessionStorage.setItem('selectedTenantUnitID', tenantUnit.id);
		this.route.navigate(['/realestates/' + this.currentRealEstateId + '/tenantunits/' + tenantUnit.id + '/uvi']);
	}

	getUviIcon(receptionType: string | undefined): string {
		switch (receptionType) {
			case 'PRINT':
				return 'outgoing_mail';
			case 'PORTAL':
				return 'devices';
			default:
				return 'mail_off';
		}
	}

	getRoleDateRange(role: { validFrom?: string; validTo?: string }): string {
		const from = role?.validFrom ? this.beautifyDate(role.validFrom) : '';
		const to = role?.validTo && !role.validTo.startsWith('9999') ? this.beautifyDate(role.validTo) : '';
		if (from && to) {
			return `${from} – ${to}`;
		}
		if (from) {
			return `${this.translocoService.translate('common.from2')} ${from}`;
		}
		return '';
	}

	private bindTenantUnits(tenantUnits: TenantUnitDetailDataSource[]): void {
		for (const tenantUnit of tenantUnits) {
			if (tenantUnit.currentResident) {
				this.energyEfficiencySettingService
					.getRoleSetting(this.currentArea, tenantUnit.currentResident?.id)
					.pipe(takeUntilDestroyed(this.destroyRef))
					.subscribe((value) => (this.receptionTypes[tenantUnit.id] = value.receptionType));
			}
		}
		this.dataSource = new MatTableDataSource(tenantUnits);
		this.dataSource.filterPredicate = this.createFilterPredicate();
		this.dataSource.paginator = this.paginator;
		this.dataSource.sortingDataAccessor = this.createSortingDataAccessor();
		this.dataSource.sort = this.sort();
	}

	/** Re-lädt die Nutzeinheiten-Tabelle (z. B. nach Speichern im Person-/Rollen-Editor). */
	private reloadTenantUnits(): void {
		if (!this.getTenantUnits) {
			return;
		}
		this.finishedLoading.set(false);
		this.getTenantUnits
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				finalize(() => this.finishedLoading.set(true))
			)
			.subscribe({
				next: (tenantUnits) => this.bindTenantUnits(tenantUnits),
			});
	}

	/**
	 * Klick auf einen Personennamen öffnet den Person-/Rollen-Editor (Nutzerwechsel) im Bearbeiten-Modus.
	 * Lädt zuerst das vollständige TenantUnitDetail (die Übersicht liefert current* ohne Rollen-Arrays).
	 */
	openPersonDialog(tenantUnit: TenantUnitDetailDataSource, roleType: string, event: Event): void {
		event.stopPropagation();
		const personId = this.getRoleByType(tenantUnit, roleType)?.person?.id;
		if (!personId) {
			return;
		}
		const area = this.currentRealEstate()?.area ?? this.currentArea;
		const isPropertyManagement = this.isPropertyManagement();
		// Die uVI-Zustellart darf nur ändern, wem die Liegenschaft das erlaubt — erst auflösen, dann öffnen.
		forkJoin([
			this.tenantUnitServiceV3.getTenantUnitById(this.currentArea, this.currentRealEstateId, tenantUnit.id),
			this.uviReceptionRightsService.resolve(area, this.currentRealEstateId),
		])
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(([detail, uviReceptionRights]) => {
				const built = this.roleModelService.buildRoleModels(detail, false);
				const roleModel = built.roleModels.find((model) => model.person?.id === personId);
				if (!roleModel) {
					return;
				}
				if (!isPropertyManagement && (roleModel.roles ?? []).some((role) => isProtectedRoleType(role.roleType))) {
					this.snackBar.warn(this.translocoService.translate('tenantunitDetails.editor.protectedRoleReadonly'));
					return;
				}
				const context: EditorTabContext = {
					// Übersicht hält die RealEstate als v1-DTO; der Editor braucht nur Area/Id (separat gesetzt).
					realEstate: null,
					tenantUnit: detail,
					realEstateArea: area,
					realEstateId: this.currentRealEstateId,
					tenantUnitId: detail.id,
					roleModels: built.roleModels,
					availablePeriodsByRoleType: built.availablePeriodsByRoleType,
					availableRoleTypes: [UiRoleType.OWNER, UiRoleType.RESIDENT, UiRoleType.TENANT],
					permissions: {},
					uviReceptionRights,
					isPropertyManagement,
				};
				const data: PersonEditorDialogData = { ...this.personEditorDataFactory.buildEditData(context, roleModel), entryContext: 'edit' };
				this.dialog
					.open(PersonEditorDialogComponent, {
						width: 'min(76rem, 96vw)',
						minWidth: '0',
						maxWidth: '96vw',
						maxHeight: '95vh',
						panelClass: 'person-editor-dialog-panel',
						autoFocus: false,
						restoreFocus: false,
						data,
					})
					.afterClosed()
					.pipe(takeUntilDestroyed(this.destroyRef))
					.subscribe((result) => {
						if (result?.saved || result?.refreshRequested) {
							this.reloadTenantUnits();
						}
					});
			});
	}

	private getRoleByType(tenantUnit: TenantUnitDetailDataSource, roleType: string): Owner | Resident | Tenant | undefined {
		switch (roleType) {
			case 'OWNER':
				return tenantUnit.currentOwner;
			case 'RESIDENT':
				return tenantUnit.currentResident;
			case 'TENANT':
				return tenantUnit.currentTenant;
			default:
				return undefined;
		}
	}

	/**
	 * Rollen-Historie für das Übersicht-Popover (vergangene + zukünftige Träger); null, wenn es außer
	 * dem heutigen keinen Eintrag gibt. Erfasste Leerstände werden ergänzt.
	 */
	private buildRoleHistory(
		list: Array<Owner | Resident | Tenant> | undefined,
		roleLabelKey: string,
		vacancies?: Array<{ validFrom?: string; validTo?: string }>
	): RoleHistory | null {
		const roleEntries = (list ?? [])
			.filter((role) => role?.person)
			.map((role) => ({
				sortKey: role.validFrom ?? '',
				status: DateUtil.classifyPeriod(role.validFrom, role.validTo),
				period: this.getRoleDateRange(role) || '—',
				name: this.personDisplayUtil.getShortNameForOverview(role.person),
			}));
		const vacancyEntries = (vacancies ?? []).map((vacancy) => ({
			sortKey: vacancy.validFrom ?? '',
			status: DateUtil.classifyPeriod(vacancy.validFrom, vacancy.validTo),
			period: this.getRoleDateRange(vacancy) || '—',
			name: this.translate('tenantunitDetails.vacancy'),
		}));
		const combined = [...roleEntries, ...vacancyEntries].sort((a, b) => a.sortKey.localeCompare(b.sortKey));
		if (!combined.some((entry) => entry.status !== 'today')) {
			return null;
		}
		const entries: RoleHistoryEntry[] = combined.map(({ status, period, name }) => ({ status, period, name }));
		return { label: this.translate(roleLabelKey), entries };
	}

	getOwnerHistory(tenantUnit: TenantUnitDetailDataSource): RoleHistory | null {
		return this.buildRoleHistory(tenantUnit.owners, 'tenantunits.owner');
	}

	getResidentHistory(tenantUnit: TenantUnitDetailDataSource): RoleHistory | null {
		return this.buildRoleHistory(tenantUnit.residents, 'tenantunits.resident', tenantUnit.vacancies);
	}

	getTenantHistory(tenantUnit: TenantUnitDetailDataSource): RoleHistory | null {
		return this.buildRoleHistory(tenantUnit.tenants, 'tenantunits.tenant');
	}

	private createFilterPredicate() {
		return (tenantUnit: TenantUnitDetailDataSource, filter: string) => {
			const accumulator = (currentTerm: string, key: string) => {
				if (key === 'address') {
					let result = currentTerm;
					for (const street of tenantUnit.address.streets) {
						result += street.streetName;
					}
					return result + tenantUnit.address.city + tenantUnit.address.zipCode;
				} else if (key === 'currentOwner') {
					return currentTerm + this.personDisplayUtil.getLongName(tenantUnit.currentOwner?.person);
				} else if (key === 'currentResident') {
					return currentTerm + this.personDisplayUtil.getLongName(tenantUnit.currentResident?.person);
				} else if (key === 'currentTenant') {
					return currentTerm + this.personDisplayUtil.getLongName(tenantUnit.currentTenant?.person);
				} else {
					return currentTerm + tenantUnit[key];
				}
			};
			let keysWithoutIds = Object.keys(tenantUnit);
			keysWithoutIds = keysWithoutIds.filter((arrayItem) => arrayItem !== 'id');
			keysWithoutIds = keysWithoutIds.filter((arrayItem) => arrayItem !== 'entranceNumber');
			keysWithoutIds = keysWithoutIds.filter((arrayItem) => arrayItem !== 'floorNumber');
			keysWithoutIds = keysWithoutIds.filter((arrayItem) => arrayItem !== 'floorType');
			const dataStr = keysWithoutIds.reduce(accumulator, '').toLowerCase();
			// Transform the filter by converting it to lowercase and removing whitespace.
			const transformedFilter = filter.trim().toLowerCase();
			return dataStr.includes(transformedFilter);
		};
	}

	private createSortingDataAccessor() {
		return (tenantUnit: TenantUnitDetailDataSource, property: string): string => {
			switch (property) {
				case 'currentOwner':
					return this.personDisplayUtil.getShortNameForOverview(tenantUnit.currentOwner?.person);
				case 'currentResident':
					return this.personDisplayUtil.getShortNameForOverview(tenantUnit.currentResident?.person);
				case 'currentTenant':
					return this.personDisplayUtil.getShortNameForOverview(tenantUnit.currentTenant?.person);
				case 'address':
					return `${tenantUnit.address?.streets?.at(0)?.streetName ?? ''} ${tenantUnit.address?.city}`;
				case 'uviDelivery':
					return this.translocoService.translate('enum.' + this.receptionTypes[tenantUnit.id]);
				default:
					return tenantUnit[property];
			}
		};
	}

	private setBreadCrumbs() {
		this.translocoService.selectTranslate('sideboard.tenantunits').subscribe({
			next: () => {
				this.breadCrumbService.reset();
				this.breadCrumbService.addBreadCrumb({
					translationKey: 'realestates.realestates',
					icon: this.icons.get('realEstate'),
				});
				this.breadCrumbService.addBreadCrumb({
					translationKey: 'sideboard.tenantunits',
					icon: this.icons.get('tenantunit'),
				});
			},
		});
	}

	private addColumns() {
		this.columns = [
			{
				label: this.translate('common.selection'),
				property: 'selectForSmokeDetectorCheckPrint',
				type: 'checkbox',
				visible: this.isSmokeDetectorRoute(),
			},
			{ label: this.translate('tenantunits.number'), property: 'number', type: 'text', visible: true },
			{
				label: this.translate('tenantunits.location'),
				property: 'location',
				type: 'text',
				visible: true,
			},
			{
				label: this.translate('tenantunits.apartmentNumber'),
				property: 'apartmentNumber',
				type: 'text',
				visible: true,
			},
			{
				label: this.translate('tenantunits.owner'),
				property: 'currentOwner',
				type: 'text',
				visible: true,
			},
			{ label: this.translate('tenantunitDetails.rolesView.showTenant'), property: 'currentTenant', type: 'text', visible: true },
			{
				label: this.translate('tenantunits.resident'),
				property: 'currentResident',
				type: 'text',
				visible: true,
			},
			{
				label: this.translate('tenantunitDetails.printUvi'),
				property: 'uviDelivery',
				type: 'text',
				visible: true,
			},
			{
				label: this.translate('address.street'),
				property: 'address',
				type: 'text',
				visible: true,
			},
			{
				label: this.translate('smokeDetectorCheck.inspectionState'),
				property: 'inspectionState',
				type: 'text',
				visible: this.isSmokeDetectorRoute(),
			},
		];
	}

	/** Selects all rows if they are not all selected; otherwise clear selection. */
	masterToggle() {
		this.selectToggleEnabled.set(!this.selectToggleEnabled());
		this.selection.clear();

		if (this.selectToggleEnabled()) {
			for (const row of this.dataSource.data) {
				this.selection.select(row);
				this.realestateById.set(row.id, row);
			}
			this.allSelected.set(true);
		} else {
			this.realestateById.clear();
			this.allSelected.set(false);
			this.deSelectedTenantunitIds = new Set();
			this.selectedTenantunitIds = new Set();
		}
	}

	private loadBillings(realEstateId: string) {
		// Die Abrechnungs-Chips sind noch nicht freigegeben: ohne dev-Rolle gar nicht erst laden.
		if (!this.sessionService.isDevRole()) {
			this.billings.set([]);
			return;
		}

		this.billingService
			.getBillingsByRealEstate(this.sessionService.effectiveArea, realEstateId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (billings) => {
					this.billings.set((billings ?? []).filter((billing) => billing.portalActive !== false));
				},
				error: () => {
					this.billings.set([]);
				},
			});
	}

	navigateToBilling(billingId: string) {
		this.route.navigate(['/billing-units', billingId]);
	}

	private getFilteredAndSortedPeriods(billing: Billing): BillingPeriod[] {
		// TODO(billingclient-v637): Billing enthält keine geschachtelten Periods mehr;
		// Periods müssen über BillingPeriodService.readAllBillingPeriod separat geladen werden.
		const periods = (billing as Billing & { periods?: BillingPeriod[] }).periods ?? [];
		if (periods.length === 0) {
			return [];
		}

		const filteredPeriods = periods.filter((period) =>
			[CustomerAccessStatus.SUBMITTED, CustomerAccessStatus.ACTIVATED].includes(period.customerAccessStatus)
		);

		return [...filteredPeriods].sort((a, b) => {
			const dateA = a.validFrom ? new Date(a.validFrom).getTime() : 0;
			const dateB = b.validFrom ? new Date(b.validFrom).getTime() : 0;
			return dateB - dateA;
		});
	}

	getRecentPeriods(billing: Billing): BillingPeriod[] {
		return this.getFilteredAndSortedPeriods(billing).slice(0, 3);
	}

	isSubmitted(period: BillingPeriod | undefined): boolean {
		return period?.customerAccessStatus === CustomerAccessStatus.SUBMITTED;
	}

	isActive(period: BillingPeriod | undefined): boolean {
		return period?.customerAccessStatus === CustomerAccessStatus.ACTIVATED;
	}
}
